import React, { useEffect, useMemo, useState } from 'react';
import { Card, Form, Row, Col, Input, Select, DatePicker, Button, Table, Space, Tag, Badge, Modal, message, PaginationProps, Popconfirm, Alert, Descriptions } from 'antd';
import { SearchOutlined, ReloadOutlined, RedoOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { rawMessageService } from '../services/whatsappConnectorService';

const { Option } = Select;
const { RangePicker } = DatePicker;
const defaultPageSize = 20;

const RawMessagesPage: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<any[]>([]);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailRecord, setDetailRecord] = useState<any>(null);
  const [resending, setResending] = useState<number | null>(null);

  const queryParams = useMemo(() => {
    const values = form.getFieldsValue();
    const params: any = {
      page,
      pageSize,
      partnerType: values.partnerType,
      phoneNumber: values.phoneNumber,
      requestId: values.requestId
    };
    const range: [Dayjs, Dayjs] | undefined = values.createdAtRange;
    if (range && range.length === 2) {
      params.startDate = range[0].format('YYYY-MM-DD HH:mm:ss');
      params.endDate = range[1].format('YYYY-MM-DD HH:mm:ss');
    }
    return params;
  }, [form, page, pageSize]);

  const loadData = async () => {
    setLoading(true);
    try {
      const res = await rawMessageService.getRawMessages(queryParams);
      setRows(res.data || []);
      setTotal(res.total || 0);
    } catch (error: any) {
      message.error('加载 Raw Messages 失败: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const onSearch = () => {
    setPage(1);
    loadData();
  };

  const onReset = () => {
    form.resetFields();
    setPage(1);
    setPageSize(defaultPageSize);
    loadData();
  };

  const handleResend = async (record: any) => {
    setResending(record.id);
    try {
      await rawMessageService.resendRawMessage(record.id);
      message.success('消息已重新发送');
      loadData();
    } catch (error: any) {
      message.error('重发失败: ' + (error.message || '未知错误'));
    } finally {
      setResending(null);
    }
  };

  const extractHeadersAndBody = (raw: any): { headers?: any; body: string } => {
    try {
      if (typeof raw === 'string') {
        const obj = JSON.parse(raw);
        const headers = obj?._headers;
        const body = JSON.stringify(obj, null, 2);
        return { headers, body };
      }
      if (typeof raw === 'object') {
        return { headers: raw._headers, body: JSON.stringify(raw, null, 2) };
      }
      return { body: String(raw) };
    } catch {
      return { body: String(raw) };
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', key: 'id', width: 90 },
    { title: 'Partner', dataIndex: 'partner_type', key: 'partner_type', width: 120, render: (v: string) => v ? <Tag color="blue">{v}</Tag> : '-' },
    { title: '手机号', dataIndex: 'phone_number', key: 'phone_number', width: 180 },
    { title: '请求ID', dataIndex: 'request_id', key: 'request_id', width: 220, ellipsis: true },
    { title: '状态', dataIndex: 'status', key: 'status', width: 100, render: (v: string) => <Badge status={v === 'received' ? 'processing' : v === 'failed' ? 'error' : 'success'} text={v || '-'} /> },
    { title: '创建时间', dataIndex: 'created_at', key: 'created_at', width: 200 },
    {
      title: '操作', key: 'action', width: 150, render: (_: any, r: any) => (
        <Space size="small">
          <Button type="link" size="small" onClick={() => { setDetailRecord(r); setDetailOpen(true); }}>查看</Button>
          <Popconfirm
            title="确认重发"
            description="确定要重新发送这条消息吗？"
            onConfirm={() => handleResend(r)}
            okText="确定"
            cancelText="取消"
          >
            <Button 
              type="link" 
              size="small" 
              icon={<RedoOutlined />}
              loading={resending === r.id}
              disabled={resending !== null && resending !== r.id}
            >
              重发
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  const onTableChange: PaginationProps['onChange'] = (p, ps) => {
    setPage(p);
    setPageSize(ps || defaultPageSize);
  };

  const hdrBody = extractHeadersAndBody(detailRecord?.raw_data);

  return (
    <Card title="Raw Messages" extra={<Button icon={<ReloadOutlined />} onClick={loadData}>刷新</Button>}>
      <Form form={form} layout="vertical" onFinish={onSearch}>
        <Row gutter={12}>
          <Col span={6}>
            <Form.Item name="partnerType" label="Partner 类型">
              <Select allowClear placeholder="请选择">
                <Option value="twilio">twilio</Option>
                <Option value="dialog360">dialog360</Option>
                <Option value="aisensy">aisensy</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="phoneNumber" label="手机号">
              <Input allowClear placeholder="例如 +1415..." />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="requestId" label="请求ID">
              <Input allowClear />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="createdAtRange" label="时间范围">
              <RangePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>
        <Row justify="end" gutter={8}>
          <Col>
            <Button onClick={onReset}>重置</Button>
          </Col>
          <Col>
            <Button type="primary" htmlType="submit" icon={<SearchOutlined />}>查询</Button>
          </Col>
        </Row>
      </Form>

      <Table
        style={{ marginTop: 16 }}
        rowKey={(r: any) => String(r.id)}
        loading={loading}
        columns={columns as any}
        dataSource={rows}
        pagination={{ current: page, pageSize, total, showSizeChanger: true, showQuickJumper: true }}
        onChange={(pagination) => onTableChange(pagination.current!, pagination.pageSize || defaultPageSize)}
      />

      <Modal
        title={`Raw Message #${detailRecord?.id || ''}`}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={<Button type="primary" onClick={() => setDetailOpen(false)}>关闭</Button>}
        width={800}
        style={{ top: 20 }}
      >
        {detailRecord && (
          <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
            {/* 基本信息 */}
            <Descriptions bordered size="small" column={2} style={{ marginBottom: 16 }}>
              <Descriptions.Item label="Partner">{detailRecord.partner_type || '-'}</Descriptions.Item>
              <Descriptions.Item label="状态">
                <Badge 
                  status={detailRecord.status === 'received' ? 'processing' : detailRecord.status === 'failed' ? 'error' : 'success'} 
                  text={detailRecord.status || '-'} 
                />
              </Descriptions.Item>
              <Descriptions.Item label="手机号">{detailRecord.phone_number || '-'}</Descriptions.Item>
              <Descriptions.Item label="创建时间">{detailRecord.created_at || '-'}</Descriptions.Item>
              <Descriptions.Item label="请求ID" span={2}>{detailRecord.request_id || '-'}</Descriptions.Item>
            </Descriptions>

            {/* 错误信息 */}
            {detailRecord.failure_reason && (
              <Alert 
                message="错误信息" 
                description={detailRecord.failure_reason} 
                type="error" 
                showIcon 
                style={{ marginBottom: 16 }}
              />
            )}

            {/* 消息内容 */}
            <Card size="small" title="消息内容" style={{ marginBottom: 0 }}>
              <pre style={{ 
                whiteSpace: 'pre-wrap', 
                wordBreak: 'break-word', 
                margin: 0,
                maxHeight: '400px',
                overflowY: 'auto',
                fontSize: '12px',
                backgroundColor: '#f5f5f5',
                padding: '12px',
                borderRadius: '4px'
              }}>
                {hdrBody.body}
              </pre>
            </Card>
          </div>
        )}
      </Modal>
    </Card>
  );
};

export default RawMessagesPage;


